import type { Answers } from '@/features/blueprint/schema/answers/Answers';
import { RecommendedPackagesDto } from '../../../../shared/dto/planSelection/RecommendedPackageDto';
import { TriggeredRecommendationKey } from '../constants/TriggeredRecommendationKeys';

export function evaluateRecommendationRules(answers: Answers): RecommendedPackagesDto {
    // Use SmartLeadForm recommendations if available
    if (answers.recommendedProducts && Array.isArray(answers.recommendedProducts)) {
        return {
            selectedPlanIds: answers.recommendedProducts as string[],
            triggeredDescriptions: generatePackageSpecificReasons(answers, answers.recommendedProducts as string[]),
        };
    }

    const defaultPackages = ["webHosting_total", "domain_managed", "branding_full"];
    return {
        selectedPlanIds: defaultPackages,
        triggeredDescriptions: generatePackageSpecificReasons(answers, defaultPackages),
    };
}

export function generatePackageSpecificReasons(answers: Answers, recommendedPackages: string[]): `triggeredRecommendations.${TriggeredRecommendationKey}`[] {
    const reasons: `triggeredRecommendations.${TriggeredRecommendationKey}`[] = [];

    // Generate reason for each recommended package
    recommendedPackages.forEach(packageId => {
        const reason = getReasonForPackage(packageId, answers);
        if (reason) {
            reasons.push(reason);
        }
    });

    return reasons;
}

function getReasonForPackage(packageId: string, answers: Answers): `triggeredRecommendations.${TriggeredRecommendationKey}` | null {
    // Website Hosting packages
    if (packageId === "webHosting_total") {
        return 'triggeredRecommendations.build.standardPackage';
    }

    if (packageId === "webHosting_pro") {
        return 'triggeredRecommendations.build.ecommercePro';
    }

    // Domain packages
    if (packageId === "domain_managed") {
        return 'triggeredRecommendations.build.domainManaged';
    }

    // SEO packages
    if (packageId === "seo_booster") {
        if (answers.goal === "getClients") {
            return 'triggeredRecommendations.goal.moreLeads';
        }
        return 'triggeredRecommendations.direct.seoOpportunity';
    }

    // Content packages
    if (packageId === "humanContent_business") {
        if (answers.involvement === "handsOff") {
            return 'triggeredRecommendations.involvement.handsOff';
        } else if (answers.goal === "getClients") {
            return 'triggeredRecommendations.content.crmIntegration';
        }
        return 'triggeredRecommendations.content.blogSeoAi';
    }

    // Analytics packages
    if (packageId === "growthCall_monthly") {
        return 'triggeredRecommendations.content.monthlyGrowthCall';
    }

    // CRO packages
    if (packageId === "cro_booster") {
        if (answers.situation === "haveWebsite" && answers.goal === "getClients") {
            return 'triggeredRecommendations.focus.ecommerce';
        }
        return 'triggeredRecommendations.optimize.professionalUx';
    }

    // Branding packages
    if (packageId.startsWith("branding_")) {
        if (answers.budget === "premium") {
            return 'triggeredRecommendations.goal.improveBrand';
        }
        return 'triggeredRecommendations.direct.brandRefresh';
    }

    // Tech Concierge packages
    if (packageId.includes("techConcierge")) {
        if (packageId === "techConcierge_free") {
            return 'triggeredRecommendations.tech.conciergeFree';
        } else if (answers.involvement === "handsOff") {
            return 'triggeredRecommendations.involvement.handsOff';
        } else if (answers.situation === "needMaintenance") {
            return 'triggeredRecommendations.goal.maintenance';
        } else if (answers.timeline === "now") {
            return 'triggeredRecommendations.urgency.immediate';
        }
        return 'triggeredRecommendations.tech.contentSupport1h';
    }

    // Content Strategy packages
    if (packageId === "content_strategy") {
        return 'triggeredRecommendations.content.keywordResearch';
    }

    return null;
}